import React, { Suspense, useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import AppLayout from '../layout/AppLayout';
import PATH from '../shared/constants/path.const';
import PageLoader from '../shared/components/PageLoader/PageLoader';
import NotFoundPage from '../pages/common/NotFound.page';
import logs from '../CHANGELOG.md';
import { isUserClient, versionRegex } from '../shared/utils/utils';
import ApplicationLogoutAPI from '../pages/common/logout/ApplicationLogoutAPI';
import { useGetNavigationMutation, useLogoutMutation } from '../services/auth/auth.api';
import { updateAuthValue } from '../services/auth/auth.slice';
import { getColors } from '../theme';

const colors = getColors();

const MasterScreen = React.lazy(() =>
  import('../pages/platform/master-screens/Master-screen.page')
);
const TimesheetApproval = React.lazy(() =>
  import('../pages/platform/timesheet-approval/TimesheetApproval')
);

const SupportTrackingSystem = React.lazy(() =>
  import('../pages/platform/support-tracking-system/SupportTrackingSystem')
);

const CommunicationTrackingSystem = React.lazy(() =>
  import('../pages/platform/communication-tracking-system/CommunicationTrackingSystem')
);

const ChangeUserPassword = React.lazy(() =>
  import('../pages/common/auth/profile/changeUserPassword.Page')
);

const TimesheetLogs = React.lazy(() => import('../pages/platform/timesheet-logs/TimesheetLogs'));

const FirstTactical = React.lazy(() => import('../pages/platform/first-tactical/FirstTactical'));

const VersionLogsPage = React.lazy(() => import('../pages/platform/version-logs/VersionLogs.Page'));

const ConsolidateReportPage = React.lazy(() =>
  import('../pages/platform/consolidate-report/ConsolidateReport')
);

const BulkTaskUploadPage = React.lazy(() =>
  import('../pages/platform/bulk-task-upload/BulkTaskUpload')
);

const Projects = React.lazy(() => import('../pages/platform/projects/Projects.Page'));

const ThemeStudioPage = React.lazy(() => import('../pages/platform/themeStudio/ThemeStudio.page'));

const EmailMappingPage = React.lazy(() =>
  import('../pages/platform/email-mapping/EmailMapping.page')
);

const isClient = isUserClient();

export const routesList = [
  {
    path: PATH.PRODUCT_DEVELOPMENT,
    name: 'Support Tracking System',
    component: SupportTrackingSystem,
  },
  {
    path: PATH.BUGS_SUGGESTIONS,
    name: 'Bugs & Suggestions',
    component: SupportTrackingSystem,
  },
  {
    path: PATH.STS_US_ROBOTICS,
    name: 'US Robotics',
    component: SupportTrackingSystem,
  },
  {
    path: PATH.COMMUNICATION_TRACKING_SYSTEM,
    name: 'Communication Tracking System',
    component: CommunicationTrackingSystem,
  },
  {
    path: PATH.TIMESHEET_LOGS,
    name: 'Timesheet Logs',
    component: TimesheetLogs,
  },
  {
    path: `${PATH.CHANGE_USER_PASSWORD}`,
    name: 'Change User Password',
    component: ChangeUserPassword,
  },
  {
    path: PATH.TIMESHEET_APPROVAL,
    name: 'Timesheet Approval',
    component: TimesheetApproval,
  },
  {
    path: PATH.MASTER_SCREEN,
    name: '',
    component: MasterScreen,
  },
  {
    path: PATH.FIRST_TACTICAL,
    name: 'First Tactical',
    component: FirstTactical,
  },
  {
    path: `${PATH.VERSION_LOGS}`,
    name: 'Version Logs',
    component: VersionLogsPage,
  },
  {
    path: `${PATH.CLIENT_STS}`,
    name: isClient ? 'Support Tracking System' : 'Client 2',
    component: SupportTrackingSystem,
  },
  {
    path: `${PATH.CONSOLIDATE_REPORT}`,
    name: 'Consolidate Report',
    component: ConsolidateReportPage,
  },
  {
    path: `${PATH.BULK_TASK_UPLOAD}`,
    name: 'Bulk Task Upload',
    component: BulkTaskUploadPage,
  },
  {
    path: `${PATH.PROJECTS}`,
    name: 'Projects',
    component: Projects,
  },
  {
    path: `${PATH.THEME_STUDIO}`,
    name: 'Theme Studio',
    component: ThemeStudioPage,
  },
  {
    path: `${PATH.EMAIL_MAPPING}`,
    name: 'Email Mapping',
    component: EmailMappingPage,
  },
];

function PrivateRoutes() {
  const { loader } = colors;
  const [logoutUser] = useLogoutMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [getNavList] = useGetNavigationMutation();

  useEffect(() => {
    if (!localStorage.getItem('navMenus')) {
      getNavList();
    } else {
      dispatch(
        updateAuthValue({ name: 'modules', value: JSON.parse(localStorage.getItem('navMenus')) })
      );
    }
  }, []);

  useEffect(() => {
    fetch(logs)
      .then((res) => res.text())
      .then((text) => {
        const latestVersion = text.match(versionRegex);
        if (latestVersion) {
          if (latestVersion[0] !== localStorage.getItem('appV') && localStorage.getItem('appV')) {
            localStorage.setItem('appV', latestVersion[0]);
            ApplicationLogoutAPI(logoutUser, dispatch, navigate, true);
          } else {
            localStorage.setItem('appV', latestVersion[0]);
          }
        }
      });
  }, [window.location.pathname]);

  return (
    <Routes>
      <Route path={PATH.HOME} element={<AppLayout />}>
        {routesList.map((route) => {
          if (route?.children) {
            return (
              <Route
                key={`route-${route.path}`}
                name={route.name}
                path={route.path}
                index={route.index}
                element={
                  <Suspense
                    fallback={<PageLoader loading={false} backgroundColor={loader.black} />}
                  >
                    <route.component />
                  </Suspense>
                }
              >
                {route.children.map((childRoute) => (
                  <Route
                    key={`route-${childRoute.path}`}
                    name={childRoute.name}
                    path={childRoute.path}
                    index={childRoute.index}
                    element={
                      <Suspense
                        fallback={
                          <PageLoader loading={false} overlay backgroundColor={loader.black} />
                        }
                      >
                        <childRoute.component />
                      </Suspense>
                    }
                  />
                ))}
              </Route>
            );
          }
          return (
            <Route
              key={`route-${route.path}`}
              name={route.name}
              path={route.path}
              index={route.index}
              element={
                <Suspense
                  fallback={<PageLoader loading={false} overlay backgroundColor={loader.black} />}
                >
                  <route.component />
                </Suspense>
              }
            />
          );
        })}
      </Route>
      <Route path={PATH.AUTH.LOGIN} element={<AppLayout />}>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default PrivateRoutes;
